<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="register-page" :class="[isDark ? 'theme-dark' : 'theme-light', currentLang === 'en' ? 'ltr-mode' : '']"
    :dir="currentLang === 'en' ? 'ltr' : 'rtl'" :lang="currentLang">
    <NavbarStyledRegister v-model:isDark="isDark" v-model:selectedLanguage="currentLang" />

    <!-- Legacy controls are kept out of the rendered page; navbar_styled_register owns them now. -->
    <div v-if="false" class="top-controls">
      <div class="lang-menu-wrap" @click.stop>
        <button class="ctrl-btn lang-toggle" @click="langOpen = !langOpen" :aria-expanded="langOpen">
          <span class="lang-flag">{{languages.find(l => l.code === currentLang)?.flag}}</span>
          <span class="lang-label">{{languages.find(l => l.code === currentLang)?.label}}</span>
          <svg class="chevron" :class="{ open: lgangOpen }" width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 4l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </button>
        <transition name="dropdown">
          <ul v-if="langOpen" class="lang-dropdown" role="listbox">
            <li v-for="lang in languages" :key="lang.code" class="lang-option"
              :class="{ active: currentLang === lang.code }" role="option" :aria-selected="currentLang === lang.code"
              @click="selectLanguage(lang.code)">
              <span>{{ lang.flag }}</span>
              <span>{{ lang.label }}</span>
            </li>
          </ul>
        </transition>
      </div>

      <button class="ctrl-btn theme-toggle" :aria-label="isDark ? t('switchToLight') : t('switchToDark')"
        @click="toggleTheme">
        <svg v-if="isDark" width="18" height="18" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="5" stroke="currentColor" stroke-width="1.8" />
          <path
            d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
            stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
        <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M21 12.79A9 9 0 1111.21 3a7 7 0 009.79 9.79z" stroke="currentColor" stroke-width="1.8"
            stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </div>

    <!-- Register layout -->
    <div class="register-container">
      <div class="register-form-col">
        <div class="form-card">
          <div class="brand-mark">
            <span class="brand-dot"></span>
          </div>

          <h2>{{ t('title') }}</h2>
          <p class="subtitle">{{ t('subtitle') }}</p>

          <form @submit.prevent="handleRegister" novalidate>
            <div class="field-group">
              <label for="phone">{{ t('phone') }}</label>
              <input id="phone" v-model="phoneNumber" type="tel" :placeholder="t('phonePlaceholder')"
                :class="{ 'input-error': error }" autocomplete="tel" inputmode="numeric" required />
              <span v-if="error" class="text-danger" role="alert">{{ error }}</span>
            </div>

            <button type="submit" class="register-btn" :disabled="loading">
              <span v-if="loading" class="btn-spinner"></span>
              {{ loading ? t('submitting') : t('submit') }}
            </button>
          </form>

          <p class="login-redirect">
            {{ t('hasAccount') }}
            <router-link to="/login" class="link-accent">{{ t('loginLink') }}</router-link>
          </p>
        </div>
      </div>

      <div class="register-image-col" aria-hidden="true">
        <div class="visual">
          <div class="hero-orbit">
            <img class="visual" :src="visual" :alt="t('brand')" />

          </div>
        </div>
      </div>

    </div>
  </div>
</template>
<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { registerService } from '@/services/api'
import visual from '@/assets/register-login-bg.svg'
import NavbarStyledRegister from '@/components/navbar_styled_register.vue'

const router = useRouter()

// Theme
const isDark = ref(true)
function toggleTheme() {
  isDark.value = !isDark.value
  localStorage.setItem('theme', isDark.value ? 'dark' : 'light')
}

// Language
const currentLang = ref('fa')
const langOpen = ref(false)

const languages = [
  { code: 'fa', label: 'فارسی', flag: '🇮🇷' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
]

function selectLanguage(code) {
  currentLang.value = code
  langOpen.value = false
  localStorage.setItem('lang', code)
  document.documentElement.setAttribute('dir', code === 'en' ? 'ltr' : 'rtl')
  document.documentElement.setAttribute('lang', code)
}

function closeLangMenu(e) {
  if (!e.target.closest('.lang-menu-wrap')) langOpen.value = false
}

// i18n
const strings = {
  fa: {
    title: 'ثبت نام',
    subtitle: 'حساب کاربری جدید در سامانه حسابداری مالی ایجاد کنید',
    phone: 'شماره موبایل',
    phonePlaceholder: '09xxxxxxxxx',
    submit: 'ثبت نام',
    submitting: 'در حال ارسال…',
    hasAccount: 'حساب دارید؟',
    loginLink: 'ورود',
    switchToLight: 'تم روشن',
    switchToDark: 'تم تیره',
    errorRequired: 'شماره موبایل الزامی است',
    errorGeneric: 'خطایی رخ داد',
  },
  en: {
    title: 'Create Account',
    subtitle: 'Enter your mobile number to get started',
    phone: 'Mobile Number',
    phonePlaceholder: '+1 555 000 0000',
    submit: 'Register',
    submitting: 'Sending…',
    hasAccount: 'Already have an account?',
    loginLink: 'Sign in',
    switchToLight: 'Light mode',
    switchToDark: 'Dark mode',
    errorRequired: 'Mobile number is required',
    errorGeneric: 'An error occurred',
  },
  ar: {
    title: 'إنشاء حساب',
    subtitle: 'أدخل رقم هاتفك للمتابعة',
    phone: 'رقم الهاتف',
    phonePlaceholder: '+966 5x xxx xxxx',
    submit: 'تسجيل',
    submitting: 'جارٍ الإرسال…',
    hasAccount: 'لديك حساب؟',
    loginLink: 'تسجيل الدخول',
    switchToLight: 'وضع النهار',
    switchToDark: 'الوضع الليلي',
    errorRequired: 'رقم الهاتف مطلوب',
    errorGeneric: 'حدث خطأ',
  },
}

function t(key) {
  return strings[currentLang.value]?.[key] ?? strings.fa[key] ?? key
}

// Form
const phoneNumber = ref('')
const loading = ref(false)
const error = ref('')

const handleRegister = async () => {
  if (!phoneNumber.value) {
    error.value = 'Phone number is required'
    return
  }

  loading.value = true
  error.value = ''

  try {
    const response = await registerService.register(phoneNumber.value)
    console.log(response)
    if (response.data.success) {
      // Store phone number for next steps
      localStorage.setItem('pendingPhoneNumber', phoneNumber.value)
      router.push('/verify-otp')
    } else {
      throw new Error(response.data.message)
    }
  } catch (err) {
    error.value = err.response?.data?.message || 'Registration failed. Please try again.'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  const savedLang = localStorage.getItem('lang')
  if (savedLang && languages.some(l => l.code === savedLang)) selectLanguage(savedLang)
  const savedTheme = localStorage.getItem('theme')
  if (savedTheme) isDark.value = savedTheme === 'dark'
  document.addEventListener('click', closeLangMenu)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', closeLangMenu)
})
</script>
<style scoped>
.register-page {
  position: relative;
  width: 100%;
  min-height: 100svh;
  overflow-x: hidden;
  font-family: 'Vazirmatn', Tahoma, 'Segoe UI', sans-serif;
  background-color: var(--bg-fallback, var(--bg));
  color: var(--text-primary);
  box-sizing: border-box;
}

:global(html),
:global(body),
:global(#app) {
  min-width: 0;
  min-height: 100%;
  margin: 0;
}

:global(body) {
  overflow-x: hidden;
}

/* Dark page background matches .landing-erp radial+linear gradient */
.theme-dark.register-page {
  background: var(--page-background);
  background-color: var(--background-fallback);
}

/* Light page background matches .light-mode gradient */
.theme-light.register-page {
  background: var(--page-background);
  background-color: var(--background-fallback);
}

.register-page *,
.register-page *::before,
.register-page *::after {
  box-sizing: border-box;
}

/* ── Top controls ── */
.top-controls {
  position: fixed;
  top: 16px;
  direction: rtl;
  inset-inline-start: 20px;
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ctrl-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--ctrl-bg);
  color: var(--text-secondary);
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  backdrop-filter: blur(12px);
  transition: background 0.18s, border-color 0.18s, color 0.18s;
}

.ctrl-btn:hover {
  background: var(--ctrl-hover);
  color: var(--text-primary);
  border-color: var(--border-accent);
}

.ctrl-btn:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.theme-toggle {
  padding: 7px 10px;
}

.lang-flag {
  font-size: 15px;
  line-height: 1;
}

.lang-label {
  font-size: 13px;
}

.chevron {
  transition: transform 0.18s ease;
  opacity: 0.7;
}

.chevron.open {
  transform: rotate(180deg);
}

.lang-menu-wrap {
  position: relative;
}

.lang-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  inset-inline-end: 0;
  min-width: 130px;
  margin: 0;
  padding: 5px;
  list-style: none;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--dropdown-bg);
  box-shadow: 0 8px 28px var(--card-shadow);
  backdrop-filter: blur(14px);
  z-index: 200;
}

.lang-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 13px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: background 0.15s;
}

.lang-option:hover {
  background: var(--ctrl-hover);
  color: var(--text-primary);
}

.lang-option.active {
  color: var(--accent);
  font-weight: 600;
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* ── Layout ── */
.register-container {
  display: grid;
  grid-template-columns: 1fr;
  width: 100%;
  min-height: 100svh;
  padding: 88px 16px 28px;
  gap: 12px;
}

.visual {
  position: relative;
  display: block;
  width: min(100%, 480px);
  height: 480px;
  aspect-ratio: 1;
  overflow: visible;
}

.hero-orbit {
  position: absolute;
  inset: 0;
  z-index: 1;
  overflow: visible;
  pointer-events: none;
}

.hero-orbit::before {
  position: absolute;
  inset: 0;
  content: '';
  background: radial-gradient(ellipse at 50% 48%, var(--orbit-glow), transparent 42%);
}

.orbit-track {
  position: absolute;
  inset: 0;
  pointer-events: none;
  transform-origin: center;
  will-change: transform;
}

.orbit-track-one {
  animation: orbit-track-one 18s linear infinite;
}

.orbit-track-two {
  animation: orbit-track-two 25s linear infinite reverse;
}

@keyframes orbit-track-one {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes orbit-track-two {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.orbit-ring {
  position: absolute;
  top: 48%;
  right: 50%;
  border: 1px solid var(--orbit-border);
  border-radius: 50%;
  transform: translate(50%, -50%) rotate(-12deg);
}

.ring-one {
  width: min(680px, 90%);
  height: 46%;
}

.ring-two {
  width: min(540px, 84%);
  height: 62%;
  border-color: rgba(240, 123, 50, 0.18);
  transform: translate(50%, -50%) rotate(18deg);
}

.core {
  position: absolute;
  top: 48%;
  right: 50%;
  width: min(290px, 62%);
  aspect-ratio: 1.16;
  height: auto;
  overflow: visible;
  transform: translate(50%, -50%);
}

.diamond-filter-defs {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
}

.diamond-core {
  filter: url(#diamond-round-corners);
}

.diamond-shape {
  position: absolute;
  inset: 0;
  clip-path: polygon(22% 0%, 78% 0%, 100% 36%, 50% 100%, 0% 36%);
  opacity: 0.76;
  
  background:
  
    radial-gradient(
      ellipse 100% 100% at 45% 28%,
      color-mix(in srgb, var(--text-primary) 26%, transparent) 5%,
      color-mix(in srgb, var(--text-primary) 14%, transparent) 40%,
      transparent 95%
    ),
    linear-gradient(
      145deg,
      color-mix(in srgb, var(--surface-hover) 25%, var(--background) 45%) 5%,
      color-mix(in srgb, var(--surface) 42%, var(--background) 58%) 35%,
      color-mix(in srgb, var(--surface) 48%, var(--background) 52%) 100%
    );
  
  }

.diamond-shape::before {
  position: absolute;
  inset: 0;
  content: '';
  background-image: radial-gradient(
    color-mix(in srgb, var(--text-primary) 7%, transparent) 1px,
    transparent 1px
  );
  background-position: center;
  background-size: 14px 14px;
  mask-image: radial-gradient(circle at 50% 45%, black 25%, transparent 72%);
  -webkit-mask-image: radial-gradient(circle at 50% 45%, black 25%, transparent 72%);
  opacity: 0.78;
  pointer-events: none;
}

.diamond-facet {
  z-index: 1;
  background:
    radial-gradient(circle, var(--glass-border-strong) 0 1.1px, transparent 1.45px) 0 0 / 8px 8px;
  opacity: 0.48;
}

.core span {
  display: none;
}

.orbit-moon {
  position: absolute;
  z-index: 5;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 25%, #ffd0a9 0 13%, #ffad70 25%, var(--accent) 56%, #a94d26 100%);
  box-shadow: 0 0 0 8px rgba(240, 123, 50, 0.1), 0 0 18px rgba(240, 123, 50, 0.62), inset -3px -3px 5px rgba(95, 34, 17, 0.28);
  animation: moon-float 5.5s ease-in-out infinite;
}

.orbit-moon::after {
  position: absolute;
  top: 3px;
  left: 4px;
  width: 4px;
  height: 4px;
  content: '';
  border-radius: 50%;
  background: #fff0dc;
  opacity: 0.9;
}

.moon-a {
  top: 11%;
  right: 25%;
}

.moon-b {
  top: 49%;
  right: 5%;
  animation-delay: -1.8s;
}

.moon-c {
  top: 84%;
  left: 20%;
  animation-delay: -3.4s;
}

@keyframes moon-float {
  0%, 100% {
    transform: translateY(0) scale(1);
  }

  50% {
    transform: translateY(-5px) scale(1.06);
  }
}

.orbit-label {
  position: absolute;
  color: var(--orbit-label);
  font-family: Tahoma, sans-serif;
  font-size: 10px;
  letter-spacing: 0.12em;
}

.label-a {
  top: 28%;
  right: 17%;
}

.label-b {
  top: 70%;
  right: 28%;
}

@media (min-width: 768px) {
  .register-container {
    grid-template-columns: minmax(0, 0.92fr) minmax(0, 1.08fr);
    align-items: center;
    min-height: 100svh;
    padding: 104px clamp(24px, 5vw, 88px) 32px;
    gap: clamp(24px, 4vw, 72px);
  }

  .register-form-col {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    min-width: 0;
    padding: 0;
  }

  [dir="ltr"] .register-form-col {
    justify-content: flex-start;
  }

  .visual {
    width: min(100%, 680px);
  }

  .register-image-col {
    height: min(72svh, 680px);
    min-width: 0;
    overflow: hidden;
    padding: 0;
  }

  .register-image-col .visual {
    flex: 0 1 680px;
  }

  .form-card {
    height: auto;
    max-height: none;
  }
}

@media (max-width: 767px) {
  .register-container {
    position: relative;
    display: block;
    min-height: 100svh;
    padding: 4px 14px 28px;
    gap: 8px;
  }

  .register-form-col {
    position: relative;
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-width: 0;
    padding: 0;
  }

  .register-image-col {
    position: absolute;
    z-index: 0;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
    min-width: 0;
    overflow: hidden;
    padding: 0;
    pointer-events: none;
  }

  .visual {
    position: absolute;
    top: 50%;
    left: 50%;
    width: min(112vw, 440px);
    transform: translate(-50%, -50%);
    max-width: 100%;
  }

  .ring-one {
    width: 112%;
    height: 46%;
  }

  .ring-two {
    width: 88%;
    height: 72%;
  }

  .core {
    width: min(235px, 66%);
    height: auto;
  }

  .moon-a {
    top: 9%;
    right: 21%;
  }

  .moon-b {
    top: 49%;
    right: 1%;
  }

  .moon-c {
    top: 85%;
    left: 15%;
  }

  .orbit-label {
    display: none;
  }
}

@media (min-width: 768px) and (max-width: 1100px) {
  .register-container {
    gap: 20px;
    padding-inline: 24px;
  }

  .register-image-col {
    height: min(62svh, 560px);
  }

  .core {
    width: min(230px, 62%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .orbit-track,
  .orbit-moon {
    animation: none;
  }
}


/* ── Card ── */
.form-card {
  display: flex;
  flex-direction: column;
  width: min(100%, 400px);
  height: 525px;
  min-height: 0;
  max-width: 400px;
  max-height: none;
  padding: 36px 32px;
  border: 1px solid var(--border);
  border-radius: 20px;
  background: #00000000 ;
  backdrop-filter: blur(4px);
  box-shadow: 0 20px 50px var(--card-shadow);
  outline: 1px solid rgba(237, 244, 240, 0.12);
  outline-offset: -1px;
  
}
@media (max-width: 768px) {
  .form-card{
    
    position: relative;
    z-index: 4;
    width: min(100%, 400px);
    padding: 28px 22px;
    background: #00000000;
    border-color: color-mix(in srgb, var(--border) 86%, var(--text-primary) 14%);
    box-shadow: 0 18px 44px color-mix(in srgb, var(--card-shadow) 82%, transparent);
  }
}


.brand-mark {
  margin-bottom: 20px;
}

.brand-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 16px var(--accent-glow);
}

h2 {
  margin: 0 0 8px;
  font-size: clamp(20px, 4vw, 26px);
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1.2;
}

.subtitle {
  margin: 0 0 28px;
  font-size: 14px;
  color: var(--text-muted);
  line-height: 1.7;
}

/* ── Form fields ── */
/* ── Form fields ── */
.field-group {
  display: grid;
  gap: 6px;
  margin-bottom: 16px;
}

.field-group label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-muted);
}

.field-group input {
  width: 100%;
  padding: 11px 14px;
  border: 1px solid var(--input-border);
  border-radius: 10px;
  background: var(--input-bg);
  color: var(--text-primary);
  font: inherit;
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.field-group input::placeholder {
  color: var(--text-muted);
}

.field-group input:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-glow);
}

.field-group input.input-error {
  border-color: #e05555;
  box-shadow: 0 0 0 3px rgba(224, 85, 85, 0.14);
}

.text-danger {
  font-size: 12px;
  color: #e07070;
  margin-top: 2px;
}

/* ── Submit button ── */
.register-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 46px;
  margin-top: 8px;
  padding: 12px 18px;
  border: none;
  border-radius: 10px;
  background: var(--accent);
  color: #fff;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 24px var(--accent-glow);
  transition: transform 0.18s, opacity 0.18s, box-shadow 0.18s;
}

.register-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px var(--accent-glow);
  background: var(--accent-soft);
}

.register-btn:disabled {
  opacity: 0.7;
  cursor: wait;
}

.btn-spinner {
  display: inline-block;
  width: 15px;
  height: 15px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Footer link ── */

@media (min-width: 768px) {
  .login-redirect {
  margin: 180px 0 0 0;
  font-size: 13px;
  color: var(--text-muted);
  text-align: center;
  }
}
@media (max-width: 768px) {
  .login-redirect {
  margin: 186px 0 0;
  font-size: 13px;
  color: var(--text-muted);
  text-align: center;
  }
}

.link-accent {
  color: var(--accent-soft);
  text-decoration: none;
  font-weight: 600;
  transition: color 0.15s;
}

.link-accent:hover {
  color: var(--accent);
  text-decoration: underline;
}

/* ── Image column ── */
.register-image-col {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  order: -1;
}

@media (min-width: 768px) {
  .register-image-col {
    justify-content: center;
    padding: 0;
  }
}

@media (min-width: 768px) {
  [dir="ltr"] .register-image-col {
    order: 0;
  }

  [dir="rtl"] .register-image-col {
    order: 0;
  }
}

/* ── Reduced motion ── */
@media (prefers-reduced-motion: reduce) {

  .register-page *,
  .register-page *::before,
  .register-page *::after {
    animation-duration: 0.01ms !important;
    transition-duration: 0.01ms !important;
  }
}
/* Remove the CSS halo; the light-specific SVG supplies its own light-theme treatment. */
.register-page.theme-light .hero-orbit::before {
  background: none;
}

.register-page.theme-light .hero-orbit > img.visual {
  mix-blend-mode: screen;
}

</style>
